import API from '../../api';
import {
  FETCH_SITE_SETTINGS_SUCCESS,
  UPDATE_SITE_SETTINGS_SUCCESS
} from './SiteSettings.constants';

const BORDER_RADIUS_MAP = {
  none: '0',
  small: '4%',
  medium: '8%',
  large: '16%',
  round: '50%'
};

const TILE_SHAPE_MAP = {
  default: { clipPath: 'none', borderRadius: null },
  circle: { clipPath: 'circle(50%)', borderRadius: '50%' },
  hexagon: {
    clipPath: 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)',
    borderRadius: '0'
  },
  diamond: {
    clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
    borderRadius: '0'
  },
  rounded: { clipPath: 'none', borderRadius: '20%' }
};

const TILE_SHADOW_MAP = {
  none: 'none',
  light: '0px 2px 2px rgba(0,0,0,0.15)',
  medium: '0px 4px 4px rgba(0,0,0,0.3)',
  heavy: '0px 6px 8px rgba(0,0,0,0.4)'
};

const FONT_SIZE_MAP = {
  small: '0.7rem',
  medium: '0.85rem',
  large: '1.1rem'
};

const TILE_SPACING_MAP = {
  tight: 4,
  normal: 10,
  wide: 16,
  'extra-wide': 24
};

const TILE_PADDING_MAP = {
  tight: '4px 2px 0 2px',
  normal: '10px 5px 0 5px',
  wide: '14px 8px 0 8px',
  'extra-wide': '18px 12px 0 12px'
};

const FONT_FAMILY_MAP = {
  default: 'inherit',
  arial: 'Arial, Helvetica, sans-serif',
  verdana: 'Verdana, Geneva, sans-serif',
  georgia: 'Georgia, Times, serif',
  'comic-sans': '"Comic Sans MS", cursive',
  courier: '"Courier New", monospace',
  trebuchet: '"Trebuchet MS", sans-serif'
};

export function applySiteSettingsCSSVars(settings) {
  const root = document.documentElement;

  // Existing color settings
  if (settings.primaryBarColor) {
    root.style.setProperty('--site-primary-bar-color', settings.primaryBarColor);
  }
  if (settings.secondaryBarColor) {
    root.style.setProperty('--site-secondary-bar-color', settings.secondaryBarColor);
  }
  if (settings.tileBackgroundColor) {
    root.style.setProperty('--site-tile-bg-color', settings.tileBackgroundColor);
  }
  if (settings.tileTextColor) {
    root.style.setProperty('--site-tile-text-color', settings.tileTextColor);
  }
  if (settings.settingsHeaderColor) {
    root.style.setProperty('--site-settings-header-color', settings.settingsHeaderColor);
  } else {
    root.style.removeProperty('--site-settings-header-color');
  }

  // Board background
  if (settings.boardBackgroundColor) {
    root.style.setProperty('--site-board-bg-color', settings.boardBackgroundColor);
  }

  // Output bar
  if (settings.outputBarColor) {
    root.style.setProperty('--site-output-bar-color', settings.outputBarColor);
  }
  if (settings.outputBarTextColor) {
    root.style.setProperty('--site-output-bar-text-color', settings.outputBarTextColor);
  }

  // Toolbar icon color
  if (settings.toolbarIconColor) {
    root.style.setProperty('--site-toolbar-icon-color', settings.toolbarIconColor);
  }

  // Selection highlight
  if (settings.selectionHighlightColor) {
    root.style.setProperty('--site-selection-highlight-color', settings.selectionHighlightColor);
  }

  // Tile border color
  if (settings.tileBorderColor) {
    root.style.setProperty('--site-tile-border-color', settings.tileBorderColor);
  }

  // Tile shape (clip-path) — takes priority over tileBorderRadius
  const shape = settings.tileShape && TILE_SHAPE_MAP[settings.tileShape];
  if (shape) {
    root.style.setProperty('--site-tile-clip-path', shape.clipPath);
    if (shape.borderRadius) {
      root.style.setProperty('--site-tile-border-radius', shape.borderRadius);
    }
    // When clip-path is active (not 'none'), use drop-shadow instead of box-shadow
    if (shape.clipPath !== 'none') {
      root.style.setProperty('--site-tile-shadow', 'none');
      const shadowVal = settings.tileShadow && TILE_SHADOW_MAP[settings.tileShadow];
      if (shadowVal && shadowVal !== 'none') {
        root.style.setProperty('--site-tile-drop-shadow', `drop-shadow(${shadowVal})`);
      } else {
        root.style.setProperty('--site-tile-drop-shadow', 'drop-shadow(0px 4px 4px rgba(0,0,0,0.3))');
      }
    }
  } else if (settings.tileBorderRadius && BORDER_RADIUS_MAP[settings.tileBorderRadius]) {
    // Only apply border radius if no shape override
    root.style.setProperty('--site-tile-border-radius', BORDER_RADIUS_MAP[settings.tileBorderRadius]);
  }

  // Tile shadow (only set if shape didn't already handle it)
  if (!shape || shape.clipPath === 'none') {
    if (settings.tileShadow && TILE_SHADOW_MAP[settings.tileShadow]) {
      root.style.setProperty('--site-tile-shadow', TILE_SHADOW_MAP[settings.tileShadow]);
    }
  }

  // Tile font
  if (settings.tileFontSize && FONT_SIZE_MAP[settings.tileFontSize]) {
    root.style.setProperty('--site-tile-font-size', FONT_SIZE_MAP[settings.tileFontSize]);
  }
  if (settings.tileFontWeight) {
    root.style.setProperty('--site-tile-font-weight', settings.tileFontWeight);
  }
  if (settings.tileFontFamily && FONT_FAMILY_MAP[settings.tileFontFamily]) {
    root.style.setProperty('--site-tile-font-family', FONT_FAMILY_MAP[settings.tileFontFamily]);
  }

  // Tiles per row
  if (settings.tilesPerRow && settings.tilesPerRow > 0) {
    root.style.setProperty('--site-tiles-per-row', settings.tilesPerRow);
  } else {
    root.style.removeProperty('--site-tiles-per-row');
  }

  // Tile spacing (grid gap + internal tile padding)
  if (settings.tileSpacing && TILE_SPACING_MAP[settings.tileSpacing] !== undefined) {
    root.style.setProperty('--site-tile-spacing', TILE_SPACING_MAP[settings.tileSpacing] + 'px');
  }
  if (settings.tileSpacing && TILE_PADDING_MAP[settings.tileSpacing]) {
    root.style.setProperty('--site-tile-padding', TILE_PADDING_MAP[settings.tileSpacing]);
  }

  // Modal template (visual style)
  root.dataset.modalTemplate = settings.modalTemplate || 'default';

  // Modal size (fullscreen / floating / halfscreen)
  root.dataset.modalSize = settings.modalSize || '';

  // Modal background (default / blurred / gradient / pattern)
  root.dataset.modalBackground = settings.modalBackground || '';

  // Tile template
  root.dataset.tileTemplate = settings.tileTemplate || '';

  // Output bar position (top / bottom / left / right)
  root.dataset.outputBarPosition = settings.outputBarPosition || '';

  // Toolbar layout (compact / sidebyside)
  root.dataset.toolbarLayout = settings.toolbarLayout || '';

  // Toolbar position (top / bottom / left / right)
  root.dataset.toolbarPosition = settings.toolbarPosition || '';
}

export function fetchSiteSettings() {
  return async dispatch => {
    try {
      const data = await API.getSiteSettings();
      applySiteSettingsCSSVars(data);
      dispatch({
        type: FETCH_SITE_SETTINGS_SUCCESS,
        payload: data
      });
    } catch (err) {
      console.error('Failed to load site settings:', err);
    }
  };
}

export function saveSiteSettings(settings, password) {
  return async dispatch => {
    const data = await API.updateSiteSettings({ ...settings, password });
    applySiteSettingsCSSVars(data);
    dispatch({
      type: UPDATE_SITE_SETTINGS_SUCCESS,
      payload: data
    });
    return data;
  };
}
